/**
 * Multi-Study Matcher - Pure Business Logic
 *
 * Implements location-first matching with priority-based cascading.
 * This is a pure class with no React dependencies, making it fully testable.
 *
 * Algorithm:
 * 1. Filter studies by zipcode eligibility
 * 2. Sort by priority (lower number = higher priority)
 * 3. Check prescreening questions in priority order
 * 4. Return first match or null if all fail
 */

import {
  Study,
  Answers,
  EligibilityResult,
  MatchResult,
} from "../types";
import { PreScreeningQuestion } from "../../../types/landingPage";

export class MultiStudyMatcher {
  constructor(private studies: Study[]) {}

  /**
   * Filter studies by zipcode eligibility
   */
  filterByLocation(zipcode: string): Study[] {
    return this.studies.filter((study) =>
      study.eligibleZipCodes.includes(zipcode)
    );
  }

  getStudies(): Study[] {
    return this.studies;
  }
  /**
   * Sort studies by priority (lower number = higher priority)
   */
  sortByPriority(studies: Study[]): Study[] {
    return [...studies].sort((a, b) => a.priority - b.priority);
  }

  /**
   * Check if patient passes prescreening questions for a specific study
   */
  checkEligibility(
    study: Study,
    commonAnswers: Answers,
    studyAnswers: Answers = {}
  ): EligibilityResult {
    // Combine common and study-specific answers
    const allAnswers = { ...commonAnswers, ...studyAnswers };

    // Check study-specific questions if they exist
    for (const question of study.questions) {
      const answer = allAnswers[question.id];

      // Skip if question not answered yet (for dynamic flow)
      if (answer === undefined || answer === null) {
        continue;
      }

      // Check if answer disqualifies
      const disqualifyResult = this.checkDisqualification(
        question,
        answer,
        allAnswers
      );

      if (disqualifyResult.disqualifies) {
        return {
          eligible: false,
          reason: disqualifyResult.reason || "Failed study-specific criteria",
        };
      }
    }

    return { eligible: true };
  }

  /**
   * Main matching function - finds the best study match for a patient
   */
  findMatch(
    zipcode: string,
    commonAnswers: Answers,
    studyAnswers: Record<string, Answers> = {}
  ): MatchResult {
    // Step 1: Filter by location
    const locationEligibleStudies = this.filterByLocation(zipcode);

    if (locationEligibleStudies.length === 0) {
      return {
        matchedStudy: null,
        studiesConsidered: [],
        studiesFailed: [],
        locationEligibleCount: 0,
      };
    }

    // Step 2: Sort by priority
    const prioritizedStudies = this.sortByPriority(locationEligibleStudies);

    const studiesConsidered = prioritizedStudies.map((s) => s.id);
    const studiesFailed: Array<{ studyId: string; reason: string }> = [];

    // Step 3: Check eligibility in priority order
    for (const study of prioritizedStudies) {
      const studySpecificAnswers = studyAnswers[study.id] || {};
      const eligibility = this.checkEligibility(
        study,
        commonAnswers,
        studySpecificAnswers
      );

      if (eligibility.eligible) {
        // Found a match!
        return {
          matchedStudy: study,
          studiesConsidered,
          studiesFailed,
          locationEligibleCount: locationEligibleStudies.length,
        };
      } else {
        // Failed this study, continue cascade
        studiesFailed.push({
          studyId: study.id,
          reason: eligibility.reason || "Unknown reason",
        });
      }
    }

    // No match found after checking all studies
    return {
      matchedStudy: null,
      studiesConsidered,
      studiesFailed,
      locationEligibleCount: locationEligibleStudies.length,
    };
  }

  /**
   * Helper: Check if a specific answer disqualifies the patient
   */
  private checkDisqualification(
    question: PreScreeningQuestion,
    answer: string | string[] | number | null | undefined,
    allAnswers: Answers
  ): { disqualifies: boolean; reason?: string } {
    // Handle radio/select questions with disqualify options
    if (question.options) {
      const selectedOptions = Array.isArray(answer) ? answer : [answer];

      for (const selectedValue of selectedOptions) {
        const option = question.options.find(
          (opt) => opt.value === selectedValue
        );

        if (option?.disqualify) {
          if (typeof option.disqualify === "function") {
            // Convert answer to the expected type for disqualify function
            const answerForDisqualify: string | string[] | undefined =
              typeof answer === "number"
                ? String(answer)
                : answer === null
                ? undefined
                : answer;
            const shouldDisqualify = option.disqualify(
              allAnswers,
              answerForDisqualify
            );
            if (shouldDisqualify) {
              return {
                disqualifies: true,
                reason:
                  option.disqualifyMessage ||
                  question.disqualifyMessage ||
                  `Selected "${option.label}"`,
              };
            }
          } else if (option.disqualify === true) {
            return {
              disqualifies: true,
              reason:
                option.disqualifyMessage ||
                question.disqualifyMessage ||
                `Selected "${option.label}"`,
            };
          }
        }
      }
    }

    // Handle BMI-based disqualification
    if (
      question.id === "weight" &&
      (question.minBMI || question.maxBMI) &&
      typeof answer === "string"
    ) {
      const heightCm = allAnswers.height
        ? parseInt(allAnswers.height as string, 10)
        : null;
      const weightLbs = parseFloat(answer);

      if (heightCm && weightLbs) {
        const bmi = this.calculateBMI(heightCm, weightLbs);

        const resolvedMinBMI = typeof question.minBMI === "function"
          ? question.minBMI(allAnswers as Record<string, string | string[]>)
          : question.minBMI;
        if (resolvedMinBMI && bmi < resolvedMinBMI) {
          return {
            disqualifies: true,
            reason: `BMI ${bmi.toFixed(1)} below minimum ${resolvedMinBMI}`,
          };
        }

        if (question.maxBMI && bmi > question.maxBMI) {
          return {
            disqualifies: true,
            reason: `BMI ${bmi.toFixed(1)} above maximum ${question.maxBMI}`,
          };
        }
      }
    }

    return { disqualifies: false };
  }

  /**
   * Helper: Calculate BMI from height (cm) and weight (lbs)
   */
  private calculateBMI(heightCm: number, weightLbs: number): number {
    const heightM = heightCm / 100;
    const weightKg = weightLbs * 0.453592;
    return weightKg / (heightM * heightM);
  }
}
